import { createClient } from "@jog/ct"
import type { ShopTheLookDetailPage, Simplify } from "@jog/types"
import { Client } from "@prismicio/client"
import { asLink, isFilled } from "@prismicio/helpers"
import type { GroupField, ImageField, LinkField, PrismicDocument } from "@prismicio/types"

import { getPaths, lowerCaseEq, retry, typedBoolean } from "../helper"
import { RedisCache } from "../redis/client"
import type { RedisShopTheLookItem } from "../redis/type"

export const buildShopTheLookPage = async <
    CoordSetAndLookDocument extends PrismicDocument<{
        products: GroupField<Simplify<{ product_link: LinkField; cover: ImageField }>>
    }>,
>(
    client: Client,
    redisItem: RedisShopTheLookItem,
    locale1?: string
): Promise<ShopTheLookDetailPage<CoordSetAndLookDocument>> => {
    const uid = redisItem.deps.uid
    if (!uid) throw new Error("The shop the look does not uid")
    const prismic = await retry(() => client.getByUID<CoordSetAndLookDocument>("coord_set_and_look", uid))
    const products = await getShopTheLookProducts(prismic, redisItem, locale1)
    return { ...redisItem, prismic, type: "shopTheLookDetail", products }
}

const getProduct = async (
    _url: LinkField,
    locale: string,
    locale1: string = locale
): Promise<Omit<ShopTheLookDetailPage<unknown>["products"][number], "cover"> | undefined> => {
    if (!isFilled.link(_url)) return
    const url = asLink(_url)
    if (!url) return
    const redisCache = new RedisCache()
    const redisItem = await redisCache.getProductId(new URL(url).pathname.replace(/^\//, ""), locale)
    if (!redisItem) return
    const ctClient = createClient()
    const res = await ctClient.product({ id: redisItem.id, locale1: locale1, locale2: locale })
    if (!res?.product) return
    return {
        id: res.product.id,
        name: res.product.masterData.current?.name || "",
        path:
            getPaths(res.product.masterData.current?.masterVariant.attributesRaw || []).find((i) =>
                lowerCaseEq(i.locale, locale)
            )?.url || "",
    }
}
const getShopTheLookProducts = async (
    prismic: { data: { products: GroupField<Simplify<{ product_link: LinkField; cover: ImageField }>> } },
    redisItem: RedisShopTheLookItem,
    locale1?: string
): Promise<ShopTheLookDetailPage<unknown>["products"]> => {
    const products = (
        await Promise.all(
            prismic.data.products.map(({ product_link, cover }) => {
                if (!isFilled.image(cover)) return
                return getProduct(product_link, redisItem.locale, locale1).then((res) =>
                    res ? { ...res, cover } : undefined
                )
            })
        )
    ).filter(typedBoolean)
    return products.filter((i) => i?.id && i?.name)
}
